import { supabase } from '../../services/supabase/client';

export interface UserActivity {
  id: string;
  user_id: string;
  activity_type: string;
  description: string;
  metadata: any;
  ip_address?: string;
  user_agent?: string;
  location?: string;
  device_info?: string;
  status: 'success' | 'warning' | 'error';
  created_at: string;
}

export class ActivityService {
  // 記錄活動
  static async logActivity(data: {
    userId: string;
    activityType: string;
    description: string;
    metadata?: any;
    ipAddress?: string;
    userAgent?: string;
    location?: string;
    deviceInfo?: string;
    status?: 'success' | 'warning' | 'error';
  }): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_activities')
        .insert({
          user_id: data.userId,
          activity_type: data.activityType,
          description: data.description,
          metadata: data.metadata || {},
          ip_address: data.ipAddress,
          user_agent: data.userAgent,
          location: data.location,
          device_info: data.deviceInfo,
          status: data.status || 'success',
        });

      if (error) {
        console.error('Error logging activity:', error);
        // 不拋出錯誤，避免影響主流程
      }
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  // 獲取用戶活動記錄
  static async getUserActivities(
    userId: string,
    filters?: {
      type?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
    }
  ): Promise<UserActivity[]> {
    try {
      let query = supabase
        .from('user_activities')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (filters?.type && filters.type !== 'all') {
        query = query.eq('activity_type', filters.type);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      } else {
        query = query.limit(100); // 默認限制100條
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching activities:', error);
        return [];
      }

      return (data || []).map((item) => ({
        id: item.id,
        user_id: item.user_id,
        activity_type: item.activity_type,
        description: item.description,
        metadata: item.metadata || {},
        ip_address: item.ip_address,
        user_agent: item.user_agent,
        location: item.location,
        device_info: item.device_info,
        status: item.status,
        created_at: item.created_at,
      }));
    } catch (error) {
      console.error('Error fetching activities:', error);
      return [];
    }
  }

  // 獲取設備信息
  static getDeviceInfo(): string {
    if (typeof navigator === 'undefined') return 'Unknown';
    
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown Browser';
  }

  // 記錄登入活動
  static async logLogin(userId: string, metadata?: any): Promise<void> {
    // 獲取IP和位置信息（可選，需要後端API支持）
    const ipAddress = metadata?.ipAddress || null;
    const location = metadata?.location || null;

    await this.logActivity({
      userId,
      activityType: 'login',
      description: '成功登入',
      metadata,
      ipAddress,
      userAgent: navigator.userAgent,
      deviceInfo: `${this.getDeviceInfo()} on ${navigator.platform}`,
      location,
      status: 'success',
    });
  }

  // 記錄登出活動
  static async logLogout(userId: string): Promise<void> {
    await this.logActivity({
      userId,
      activityType: 'logout',
      description: '成功登出',
      status: 'success',
    });
  }

  // 批量刪除活動記錄
  static async deleteActivities(activityIds: string[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('user_activities')
        .delete()
        .in('id', activityIds);

      if (error) {
        console.error('Error deleting activities:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error deleting activities:', error);
      throw error;
    }
  }

  // 獲取活動統計
  static async getActivityStats(
    userId: string,
    filters?: {
      startDate?: Date;
      endDate?: Date;
    }
  ): Promise<{
    total: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
  }> {
    try {
      let query = supabase
        .from('user_activities')
        .select('activity_type, status')
        .eq('user_id', userId);

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate.toISOString());
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching activity stats:', error);
        return { total: 0, byType: {}, byStatus: {} };
      }

      const byType: Record<string, number> = {};
      const byStatus: Record<string, number> = { success: 0, warning: 0, error: 0 };

      (data || []).forEach((item: any) => {
        byType[item.activity_type] = (byType[item.activity_type] || 0) + 1;
        byStatus[item.status] = (byStatus[item.status] || 0) + 1;
      });

      return {
        total: data?.length || 0,
        byType,
        byStatus,
      };
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      return { total: 0, byType: {}, byStatus: {} };
    }
  }
}

